import { useState } from 'react';
import type { MatchResponse, MatchedOffer, QuizAnswers } from '../types';
import type { ApiClient } from '../api';
import { HorseRace } from './HorseRace';

export function Settlement({ answers }: { answers: QuizAnswers }) {
  return (
    <section className="fb-step fb-photofinish">
      <div className="fb-photofinish__rays" aria-hidden="true" />
      <div className="fb-photofinish__inner">
        <div className="fb-photofinish__trophy">🏆</div>
        <div className="fb-photofinish__label">Photo Finish</div>
        <h2 className="fb-photofinish__heading">
          Winner: <span>YOU</span>
        </h2>
        <div className="fb-photofinish__race">
          <HorseRace answers={answers} step={6} totalSteps={5} finishing />
        </div>
        <div className="fb-photofinish__msg">Weighing in your free-bet matches…</div>
      </div>
    </section>
  );
}

interface Props {
  api: ApiClient;
  data: MatchResponse | null;
  loading: boolean;
  error: string | null;
  onRestart: () => void;
}

export function Results({ api, data, loading, error, onRestart }: Props) {
  if (error) {
    return (
      <section className="fb-step fb-results">
        <div className="fb-results__body">
          <div className="fb-error">{error}</div>
        </div>
        <footer className="fb-results__footer">
          <button type="button" className="fb-results__restart" onClick={onRestart}>
            ← Start Over
          </button>
        </footer>
      </section>
    );
  }

  const matches = data?.matches ?? [];

  return (
    <section className="fb-step fb-results">
      <ResultsHeader
        loading={loading}
        persona={data?.personaSummary ?? null}
        matchCount={matches.length}
      />
      <div className="fb-results__body">
        {loading && <ResultSkeletons count={3} />}
        {!loading && matches.length === 0 && (
          <div className="fb-empty">
            <h3 style={{ margin: '0 0 8px' }}>No strong matches this time.</h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--fb-light-text-secondary)' }}>
              Try widening your stake range or sport selection. Meanwhile, browse the
              full offers list at freebets.com/free-bets.
            </p>
          </div>
        )}
        {!loading &&
          matches.map((m) => <OfferCard key={m.offerId} api={api} offer={m} />)}
      </div>
      <footer className="fb-results__footer">
        <button
          type="button"
          className="fb-results__restart"
          onClick={onRestart}
          aria-label="Start over"
        >
          ← Start over
        </button>
        {data?.complianceHtml && (
          <div
            className="fb-compliance-mount"
            style={{ width: '100%' }}
            dangerouslySetInnerHTML={{ __html: data.complianceHtml }}
          />
        )}
      </footer>
    </section>
  );
}

function ResultsHeader({
  loading,
  persona,
  matchCount,
}: {
  loading: boolean;
  persona: string | null;
  matchCount: number;
}) {
  const heading = loading
    ? 'Finding your matches…'
    : matchCount === 0
    ? 'No strong matches this time'
    : matchCount === 1
    ? 'Your top match'
    : `Your top ${matchCount} matches`;
  return (
    <header className="fb-results-header" aria-live="polite">
      <div className="fb-results-header__pillrow">
        <span className="fb-results-header__pill">Find Your Free Bet</span>
        <span className="fb-results-header__sub">Personalised for you</span>
      </div>
      <h2 className="fb-results-header__heading">{heading}</h2>
      {loading ? (
        <p className="fb-results-header__loading">
          ✨ Personalising your recommendations
        </p>
      ) : (
        <p className="fb-results-header__persona">{persona}</p>
      )}
    </header>
  );
}

function ResultSkeletons({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="fb-skeleton" aria-hidden="true">
          <div className="fb-skeleton__line fb-skeleton__line--short" />
          <div className="fb-skeleton__line fb-skeleton__line--medium" />
          <div className="fb-skeleton__line" />
          <div className="fb-skeleton__line fb-skeleton__line--medium" />
        </div>
      ))}
    </>
  );
}

function OfferCard({ api, offer }: { api: ApiClient; offer: MatchedOffer }) {
  const [showTerms, setShowTerms] = useState(false);
  const featured = offer.isBestMatch;
  return (
    <article className={'fb-card' + (featured ? ' fb-card--featured' : '')}>
      {featured && <div className="fb-card__strip">⭐ Your Best Match Today</div>}
      <div className="fb-card__body">
        <div className="fb-card__top">
          <div
            className="fb-card__logo"
            style={{ background: offer.brand.logoBgColor }}
            aria-hidden="true"
          >
            {offer.brand.logoUrl ? (
              <img
                src={offer.brand.logoUrl}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'inherit',
                }}
              />
            ) : (
              offer.brand.logoInitials
            )}
          </div>
          <div className="fb-card__title-block">
            <div className="fb-card__brand-name">{offer.brand.name}</div>
            <h3 className="fb-card__title">{offer.title}</h3>
          </div>
          <div className="fb-card__badge-block">
            <span className={'fb-badge fb-badge--' + offer.matchBadge}>
              {labelForBadge(offer.matchBadge)}
            </span>
            <div className="fb-card__match">{offer.matchScore}% match</div>
          </div>
        </div>
        <div className="fb-card__progress" aria-hidden="true">
          <div
            className="fb-card__progress-fill"
            style={{ width: `${offer.matchScore}%` }}
          />
        </div>
        {offer.reasoningPills.length > 0 && (
          <div className="fb-card__pills">
            {offer.reasoningPills.map((p, i) => (
              <span key={i} className="fb-pill">
                ✓ {p.label}
              </span>
            ))}
          </div>
        )}
        <div className="fb-explain">
          {offer.explanation}
          {offer.watchout && (
            <span className="fb-explain__watchout">{offer.watchout}</span>
          )}
        </div>
        <div className="fb-terms">
          <button
            type="button"
            className="fb-terms__toggle"
            onClick={() => setShowTerms((v) => !v)}
            aria-expanded={showTerms}
          >
            {showTerms ? 'Hide T&Cs' : 'View T&Cs'}
          </button>
          {showTerms && (
            <div className="fb-terms__body">
              <p>
                T&Cs:{' '}
                {offer.promoCode && (
                  <>
                    Use code <strong>{offer.promoCode}</strong>.{' '}
                  </>
                )}
                &pound;{offer.minQualifyingStake} min
                {offer.minOddsFractional && <> at {offer.minOddsFractional}+</>}.{' '}
                {offer.freeBetSplit ?? `\u00A3${offer.freeBetValue} in free bets.`}
              </p>
              <p>
                Min stake: &pound;{offer.minQualifyingStake}
                {offer.minOddsDecimal !== null && (
                  <> · Min odds: {offer.minOddsDecimal}</>
                )}
                {offer.brand.ukgcLicense && <> · Licence: {offer.brand.ukgcLicense}</>}
              </p>
              {offer.termsFull && <p>{offer.termsFull}</p>}
            </div>
          )}
        </div>
        <a
          className={'fb-cta ' + (featured ? 'fb-cta--primary' : 'fb-cta--secondary')}
          href={api.claimUrl(offer.claimUrl)}
          target="_blank"
          rel="sponsored noopener nofollow"
        >
          Claim at {offer.brand.name} →
        </a>
      </div>
    </article>
  );
}

function labelForBadge(b: 'strong' | 'good' | 'fair'): string {
  return b === 'strong' ? 'Strong Match' : b === 'good' ? 'Good Match' : 'Fair Match';
}
